import React, {useEffect} from 'react'
import {Checkbox, FormControlLabel, Stack, TextField} from "@mui/material";
import {Controller, useForm} from 'react-hook-form'
import {FormIds} from "../../constants";
import {useTranslation} from "react-i18next";
import {useUserRole} from "../../utils/UserRoleContext";

export interface SalesPersonFormValues {
  lastName: string,
  disabled: boolean
}

const defaultTextFieldWidth = {
  width: "15rem"
}

interface SalesPersonFormProps {
  onSubmit: (data: SalesPersonFormValues) => void
  /** This function updates the dirty state in the parents component */
  updateDirty: (dirty: boolean) => void
  defaultValues: SalesPersonFormValues
  /**
   * A last name the backend rejected as already taken, shown as an error on the field itself.
   * Only applies while the input still holds that name — typing a different one clears it.
   */
  duplicateLastName?: string | null
  /** Called when the user edits away from the rejected name, so the parent can drop it. */
  onDuplicateResolved?: () => void
}

export default function SalesPersonForm({
                                          onSubmit,
                                          defaultValues,
                                          updateDirty,
                                          duplicateLastName,
                                          onDuplicateResolved,
                                        }: Readonly<SalesPersonFormProps>) {

  const form = useForm<SalesPersonFormValues>({
    defaultValues
  })

  const {t} = useTranslation()
  const {isAdmin} = useUserRole();
  const {register, handleSubmit, control, formState, getValues, setValue, watch} = form
  const {errors} = formState
  const columnSpacing = 5

  useEffect(() => {
    updateDirty(form.formState.isDirty)
  }, [form.formState.isDirty])

  // Compare trimmed and case-insensitively: the backend's uniqueness check is case-insensitive,
  // so re-submitting the same name in a different case would be rejected again.
  const currentLastName = watch('lastName')
  // Uniqueness is only enforced for active salespeople, so marking the record disabled makes the
  // rejected name allowed again -- treat that exactly like editing the name away, otherwise the
  // save the backend would accept stays blocked behind a stale error.
  const currentDisabled = watch('disabled')
  const stillShowingRejectedName = !!duplicateLastName
    && !currentDisabled
    && currentLastName?.trim().toLowerCase() === duplicateLastName.trim().toLowerCase()

  useEffect(() => {
    if (duplicateLastName && !stillShowingRejectedName) {
      onDuplicateResolved?.()
    }
  }, [stillShowingRejectedName, duplicateLastName])

  const lastNameError = errors.lastName?.message
    ?? (stillShowingRejectedName ? t('form.fields.salesperson.lastNameNotUnique') : undefined)

  return (
    <form id={FormIds.updateSalesPerson} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <TextField
            sx={defaultTextFieldWidth}
            id={"lastName"}
            variant="standard"
            label={t('form.fields.salesperson.lastName')}
            required
            {...register("lastName", {
              required: t('form.fields.salesperson.lastNameRequired')
            })}
            error={!!lastNameError}
            helperText={lastNameError}
            disabled={!isAdmin}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={columnSpacing}
        >
          <Controller
            name={'disabled'}
            control={control}
            render={({field}) => <FormControlLabel control={<Checkbox checked={Boolean(field.value)} {...field} disabled={!isAdmin} />}
                                                   label={t('form.fields.salesperson.disabled')}/>}
          />

        </Stack>
      </Stack>
    </form>
  )
}
